/**
 * Colour arithmetic for the colours a business picks in Site & widget.
 *
 * Every value that reaches this file comes from stored settings, so each helper
 * accepts anything and returns null (or leaves the caller's default) for what is
 * not a hex colour. A stored string interpolated into a CSS custom property
 * unchecked can close the declaration and add its own.
 */

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** "#abc" → "#aabbcc"; anything that is not a hex colour → null. */
export const normalizeHex = (value) => {
  if (!HEX_COLOR.test(value || "")) return null;
  const hex = value.slice(1);
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  return `#${full.toLowerCase()}`;
};

const channels = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

const toHex = (values) =>
  `#${values.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;

/** WCAG relative luminance of a normalized "#rrggbb". */
export const luminance = (hex) => {
  const [r, g, b] = channels(hex).map((c) => {
    const value = c / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two normalized colours. */
export const contrastRatio = (a, b) => {
  const [high, low] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (high + 0.05) / (low + 0.05);
};

/**
 * White or near-black, whichever reads better on `hex`. A business whose colour
 * is pale yellow would otherwise get white labels nobody can read.
 */
export const readableTextOn = (hex, dark = "#1a1d19") =>
  contrastRatio(hex, "#ffffff") >= contrastRatio(hex, dark) ? "#ffffff" : dark;

/**
 * The label colour on a business's brand colour, wherever it fills a button:
 * white whenever white holds the 3:1 bold UI text needs — the look of
 * EasyPlanner's own green buttons, and what businesses expect on theirs — and
 * the more readable of the two only for a colour pale enough to wash white out.
 * The site, the widget's form and the widget's floating button all use it, so
 * one business's buttons do not disagree with each other. `public/embed.js`
 * carries a copy, being dependency-free.
 */
export const onBrandText = (hex, dark = "#1a1d19") =>
  contrastRatio(hex, "#ffffff") >= 3 ? "#ffffff" : readableTextOn(hex, dark);

/** `hex` with every channel reduced by `amount` (0–1): a hover state. */
export const darken = (hex, amount) => toHex(channels(hex).map((c) => c * (1 - amount)));

/** `hex` laid over white at `weight` (0–1): the tinted surfaces behind content. */
export const tint = (hex, weight) =>
  toHex(channels(hex).map((c) => 255 + (c - 255) * weight));
